/**
 * Steuerprüfung L6 (0445): Prüfung der USt-IdNr. bei VIES, der Schnittstelle der EU-Kommission („VIES on-the-Web“, REST).
 * Abfrage per POST an VIES_URL mit { countryCode, vatNumber, requesterMemberStateCode?, requesterNumber? }; Antwort
 * { countryCode, vatNumber, requestDate, valid, requestIdentifier, name, address } bzw. bei Fehlern
 * { actionSucceed: false, errorWrappers: [{ error, message? }] } (Vertrag: swagger_publicVAT.yaml der Kommission, abgerufen
 * 24.10.2026). Die Kennung der Anfrage (requestIdentifier) liefert VIES nur, wenn die eigene USt-IdNr. als Anfrager mitgeht;
 * sie ist der Nachweis der Abfrage (vergleichbar mit der qualifizierten Bestätigung). Name und Anschrift liefern nicht alle
 * Mitgliedstaaten (Deutschland nie); dann steht „---“ in der Antwort.
 * Abgefragt werden nur Nummern mit dem Präfix eines Mitgliedstaats (EL für Griechenland, XI für Nordirland, das VIES weiter führt).
 * Nummern aus Großbritannien (GB, seit 2021 nicht mehr in VIES), der Schweiz und aus Drittländern fragt das Produkt nicht ab.
 */

export const VIES_URL = "https://ec.europa.eu/taxation_customs/vies/rest-api/check-vat-number";
/** Zeitüberschreitung je Abfrage */
export const VIES_TIMEOUT_MS = 6000;
/** Gültig bzw. ungültig gilt je Nummer und Agentur so lange als aktuell (Zwischenspeicher und Nachweis beim Festschreiben) */
export const VIES_CACHE_STUNDEN = 24;
/** Eigene Grenze je Agentur: höchstens so viele neue Abfragen in 10 Minuten (VIES drosselt selbst, MS_MAX_CONCURRENT_REQ) */
export const VIES_GRENZE_10_MIN = 30;

const VIES_LAENDER = ["AT", "BE", "BG", "CY", "CZ", "DE", "DK", "EE", "EL", "ES", "FI", "FR", "HR", "HU", "IE", "IT", "LT", "LU", "LV",
  "MT", "NL", "PL", "PT", "RO", "SE", "SI", "SK", "XI"] as const;

export type ViesZiel =
  | { art: "eu"; norm: string; land: string; nummer: string }
  /** gb: Vereinigtes Königreich ohne Nordirland; ch: Schweiz; format: Präfix eines Mitgliedstaats, aber keine gültige Form */
  | { art: "gb" | "ch" | "drittland" | "format" | "leer"; norm: string };

/** Schreibweise wie app.vat_id_norm: Großbuchstaben ohne Leerzeichen, Punkte, Binde- und Schrägstriche; GR wird EL (VIES-Kennung). */
export function ustIdNorm(value: string | null | undefined): string {
  const v = (value ?? "").replace(/[\s.\-/]/g, "").toUpperCase();
  return v.startsWith("GR") ? `EL${v.slice(2)}` : v;
}

/** Wird diese Nummer bei VIES abgefragt, und wenn nicht, warum nicht? */
export function viesZiel(value: string | null | undefined): ViesZiel {
  const norm = ustIdNorm(value);
  if (!norm) return { art: "leer", norm };
  const land = norm.slice(0, 2);
  if ((VIES_LAENDER as readonly string[]).includes(land)) {
    const nummer = norm.slice(2);
    return /^[0-9A-Z+*]{2,12}$/.test(nummer) ? { art: "eu", norm, land, nummer } : { art: "format", norm };
  }
  if (land === "GB") return { art: "gb", norm };
  if (land === "CH") return { art: "ch", norm };
  return { art: "drittland", norm };
}

export type ViesStatus = "valid" | "invalid" | "unavailable";
export interface ViesErgebnis {
  status: ViesStatus;
  name: string | null;
  address: string | null;
  requestIdentifier: string | null;
  requestDate: string | null;
  /** Fehlerkennung von VIES oder der Verbindung (TIMEOUT, MS_UNAVAILABLE, HTTP_503 …) */
  fehler: string | null;
}

const text = (v: unknown): string | null => {
  if (typeof v !== "string") return null;
  const s = v.trim();
  return s && s !== "---" ? s.slice(0, 500) : null;
};

/**
 * Antwort von VIES lesen. `mitAnfrager`: die Abfrage trug die eigene USt-IdNr.; dann kann eine Formatmeldung (VOW-ERR-…) auch die
 * eigene Nummer betreffen und zählt nicht als „ungültig“. Ohne Anfrager betrifft sie die abgefragte Nummer.
 */
export function viesAntwortLesen(httpStatus: number, body: unknown, mitAnfrager: boolean): ViesErgebnis {
  const b = (body && typeof body === "object" ? body : {}) as Record<string, unknown>;
  const leer = { name: null, address: null, requestIdentifier: null, requestDate: null };
  if (httpStatus === 200 && typeof b.valid === "boolean") {
    return {
      status: b.valid ? "valid" : "invalid", name: b.valid ? text(b.name) : null, address: b.valid ? text(b.address) : null,
      requestIdentifier: text(b.requestIdentifier), requestDate: text(b.requestDate), fehler: null,
    };
  }
  const wrappers = Array.isArray(b.errorWrappers) ? (b.errorWrappers as { error?: unknown }[]) : [];
  const code = text(wrappers[0]?.error) ?? `HTTP_${httpStatus}`;
  // INVALID_INPUT: die Nummer kann in diesem Land nicht gültig sein
  if (code === "INVALID_INPUT" || (!mitAnfrager && code.startsWith("VOW-ERR-26"))) return { status: "invalid", ...leer, fehler: code };
  return { status: "unavailable", ...leer, fehler: code };
}

/** Lohnt eine zweite Abfrage ohne die eigene USt-IdNr.? (die eigene Nummer wurde abgelehnt) */
export function viesOhneAnfragerWiederholen(e: ViesErgebnis): boolean {
  return e.status === "unavailable" && !!e.fehler && (e.fehler === "INVALID_REQUESTER_INFO" || e.fehler.startsWith("VOW-ERR-"));
}
